import type { Game } from '@/lib/types';
import type { AggregateStats, GameStatEntry, PlayerDetail, PlayerGameEntry, VenueStatEntry } from '@/lib/types/insights';
import type { LeaderboardEntry } from '@/lib/types';

export function calculateAggregateStats(games: Game[]): AggregateStats {
  const archived = games.filter(g => (g.status === 'archived' || g.status === 'settled' || g.status === 'settling') && !g.is_deleted);
  if (archived.length === 0) {
    return { totalGames: 0, totalWagered: 0, biggestPot: 0, avgPlayersPerGame: 0 };
  }

  const totalGames = archived.length;
  let totalWagered = 0;
  let biggestPot = 0;
  let totalPlayers = 0;

  for (const game of archived) {
    const pot = game.players.reduce((s, p) => s + (p.total_bought_in || 0), 0);
    totalWagered += pot;
    if (pot > biggestPot) biggestPot = pot;
    totalPlayers += game.players.length;
  }

  return {
    totalGames,
    totalWagered,
    biggestPot,
    avgPlayersPerGame: Math.round((totalPlayers / totalGames) * 10) / 10,
  };
}

export function calculateGameStats(games: Game[]): GameStatEntry[] {
  const archived = games.filter(g => (g.status === 'archived' || g.status === 'settled' || g.status === 'settling') && !g.is_deleted);

  const entries: GameStatEntry[] = archived.map(game => {
    const potSize = game.players.reduce((s, p) => s + (p.total_bought_in || 0), 0);
    let winner: { name: string; pnl: number } | null = null;
    let biggestLoser: { name: string; pnl: number } | null = null;

    for (const player of game.players) {
      const finalStack = game.settlement?.player_inputs[player.user_id]?.final_stack ?? 0;
      const pnl = finalStack - (player.total_bought_in || 0);
      if (!winner || pnl > winner.pnl) winner = { name: player.name, pnl };
      if (!biggestLoser || pnl < biggestLoser.pnl) biggestLoser = { name: player.name, pnl };
    }

    return {
      gameId: game.id,
      date: game.date,
      venue: game.venue,
      playerCount: game.players.length,
      potSize,
      winner,
      biggestLoser,
      spread: winner && biggestLoser ? winner.pnl - biggestLoser.pnl : 0,
    };
  });

  return entries.sort((a, b) => b.potSize - a.potSize || new Date(b.date).getTime() - new Date(a.date).getTime());
}

export function calculatePlayerDetail(games: Game[], userId: string): PlayerDetail {
  const archived = games.filter(g => (g.status === 'archived' || g.status === 'settled' || g.status === 'settling') && !g.is_deleted);
  const timeline: PlayerGameEntry[] = [];
  let currentStreak = 0;
  let currentStreakType: 'win' | 'loss' | null = null;
  let longestWin = 0;
  let longestLoss = 0;
  let bestWin: PlayerDetail['bestWin'] = null;
  let worstLoss: PlayerDetail['worstLoss'] = null;
  const monthlyMap = new Map<string, { gameCount: number; totalPnl: number }>();

  // Sort games chronologically for streak computation
  const sorted = [...archived].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  for (const game of sorted) {
    const player = game.players.find(p => p.user_id === userId);
    if (!player) continue;

    const finalStack = game.settlement?.player_inputs[userId]?.final_stack ?? 0;
    const pnl = finalStack - (player.total_bought_in || 0);

    timeline.push({ date: game.date, venue: game.venue, pnl });

    // Track best win / worst loss
    if (pnl > 0 && (!bestWin || pnl > bestWin.pnl)) {
      bestWin = { pnl, date: game.date, venue: game.venue };
    }
    if (pnl < 0 && (!worstLoss || pnl < worstLoss.pnl)) {
      worstLoss = { pnl, date: game.date, venue: game.venue };
    }

    // Streaks
    if (pnl > 0) {
      if (currentStreakType === 'win') currentStreak++;
      else { currentStreak = 1; currentStreakType = 'win'; }
      if (currentStreak > longestWin) longestWin = currentStreak;
    } else if (pnl < 0) {
      if (currentStreakType === 'loss') currentStreak++;
      else { currentStreak = 1; currentStreakType = 'loss'; }
      if (currentStreak > longestLoss) longestLoss = currentStreak;
    } else {
      currentStreak = 0;
      currentStreakType = null;
    }

    // Monthly breakdown
    const monthKey = game.date.substring(0, 7); // "2026-05"
    const monthData = monthlyMap.get(monthKey) || { gameCount: 0, totalPnl: 0 };
    monthData.gameCount++;
    monthData.totalPnl += pnl;
    monthlyMap.set(monthKey, monthData);
  }

  // Recalculate current streak (reverse pass to find current)
  let revStreak = 0;
  let revType: 'win' | 'loss' | null = null;
  for (let i = timeline.length - 1; i >= 0; i--) {
    const pnl = timeline[i].pnl;
    if (pnl > 0) {
      if (revType === 'win') revStreak++;
      else { revStreak = 1; revType = 'win'; }
    } else if (pnl < 0) {
      if (revType === 'loss') revStreak++;
      else { revStreak = 1; revType = 'loss'; }
    } else break;
  }

  const monthlyBreakdown = Array.from(monthlyMap.entries())
    .map(([month, data]) => ({
      month,
      gameCount: data.gameCount,
      totalPnl: data.totalPnl,
    }))
    .sort((a, b) => b.month.localeCompare(a.month));

  return {
    timeline,
    longestWinStreak: longestWin,
    longestLossStreak: longestLoss,
    currentStreak: revStreak,
    currentStreakType: revType,
    bestWin,
    worstLoss,
    monthlyBreakdown,
  };
}

export function calculateVenueStats(games: Game[]): VenueStatEntry[] {
  const archived = games.filter(g => (g.status === 'archived' || g.status === 'settled' || g.status === 'settling') && !g.is_deleted);
  const venueMap = new Map<string, { games: number; totalWagered: number; totalPlayers: number; playerPnl: Map<string, { name: string; pnl: number; games: number }> }>();

  for (const game of archived) {
    if (!venueMap.has(game.venue)) {
      venueMap.set(game.venue, { games: 0, totalWagered: 0, totalPlayers: 0, playerPnl: new Map() });
    }
    const v = venueMap.get(game.venue)!;
    v.games++;
    const pot = game.players.reduce((s, p) => s + (p.total_bought_in || 0), 0);
    v.totalWagered += pot;
    v.totalPlayers += game.players.length;

    for (const player of game.players) {
      const finalStack = game.settlement?.player_inputs[player.user_id]?.final_stack ?? 0;
      const pnl = finalStack - (player.total_bought_in || 0);
      const existing = v.playerPnl.get(player.user_id) || { name: player.name, pnl: 0, games: 0 };
      existing.pnl += pnl;
      existing.games++;
      v.playerPnl.set(player.user_id, existing);
    }
  }

  return Array.from(venueMap.entries()).map(([venueName, v]) => {
    let dominantPlayer: VenueStatEntry['dominantPlayer'] = null;
    let bestPnl = -Infinity;
    let bestGames = 0;
    for (const [, pd] of v.playerPnl) {
      if (pd.pnl > bestPnl || (pd.pnl === bestPnl && pd.games > bestGames)) {
        bestPnl = pd.pnl;
        bestGames = pd.games;
        dominantPlayer = { name: pd.name, totalPnl: pd.pnl, gamesPlayed: pd.games };
      }
    }

    return {
      venueName,
      gamesPlayed: v.games,
      totalWagered: v.totalWagered,
      avgPlayers: Math.round((v.totalPlayers / v.games) * 10) / 10,
      avgPot: Math.round((v.totalWagered / v.games) * 10) / 10,
      dominantPlayer,
    };
  }).sort((a, b) => b.gamesPlayed - a.gamesPlayed);
}

export function calculatePlayerRank(entries: Pick<LeaderboardEntry, 'user_id' | 'total_pnl'>[], userId: string): number {
  const sorted = [...entries].sort((a, b) => b.total_pnl - a.total_pnl);
  const idx = sorted.findIndex(e => e.user_id === userId);
  return idx >= 0 ? idx + 1 : 0;
}
